import React, { useState } from "react";
import {
  Box,
  Button,
  Container,
  Step,
  StepLabel,
  Stepper,
  Typography,
  useTheme,
  StepConnector,
  styled,
  alpha,
  StepIconProps,
} from "@mui/material";
import stepConnectorClasses from "@mui/material/StepConnector/stepConnectorClasses";
import { Formik, Form, FormikProps } from "formik";
import * as yup from "yup";
import StepTwoBusinessDetails from "./StepTwoBussinessDetails";

import StepThreeReview from "./StepThreeBussinessReview";
import StepOneBusinessType from "./StepOneBussinessType";

/**************************
 * Types & Schema
 *************************/
export interface BusinessFormValues {
  businessType: "CTP" | "PTP" | "VTP";
  businessName: string;
  country: string;
  address: string;
  registrationNumber?: string | null;
  contactName: string;
  contactPhone: string;
  contactCountryCode: string;
  businessEmail: string;
  emailDomain: string;
  password: string;
}

const denyList = [
  "gmail.com",
  "outlook.com",
  "hotmail.com",
  "icloud.com",
  "naver.com",
  "yahoo.com",
];

const BusinessSchema: yup.ObjectSchema<BusinessFormValues> = yup
  .object({
    businessType: yup
      .mixed<"CTP" | "PTP" | "VTP">()
      .oneOf(["CTP", "PTP", "VTP"])
      .required("Please select a business type"),

    businessName: yup.string().required("Business name is required"),

    country: yup.string().required("Country is required"),

    address: yup.string().required("Address is required"),

    registrationNumber: yup.string().nullable().optional(),

    contactName: yup.string().required("Contact name is required"),

    contactPhone: yup.string().required("Contact phone is required"),

    contactCountryCode: yup.string().required("Country code is required"),

    password: yup
      .string()
      .min(8, "Password must be at least 8 characters")
      .required("Password is required"),

    businessEmail: yup
      .string()
      .email("Invalid email")
      .required("Business email is required")
      .test(
        "corporate-domain",
        "Please enter a valid business email (no Gmail, Outlook, etc.)",
        (val) => {
          if (!val) return false;
          const domain = val.split("@")[1]?.toLowerCase();
          return domain ? !denyList.includes(domain) : false;
        }
      ),

    emailDomain: yup
      .string()
      .matches(/^[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/, "Invalid domain format")
      .required("Email domain is required")
      .test(
        "corporate-domain",
        "Please enter a valid email domain (no Gmail, Outlook, etc.)",
        (val) => {
          if (!val) return false;
          return !denyList.includes(val.toLowerCase());
        }
      ),
  })
  .test(
    "match-email-domain",
    "Email domain must match Business Email domain",
    (values) => {
      if (!values.businessEmail || !values.emailDomain) return true;
      const emailDomainPart = values.businessEmail.split("@")[1]?.toLowerCase();
      return emailDomainPart === values.emailDomain.toLowerCase();
    }
  );
/**************************
 * Brand Tokens
 *************************/
const GRADIENT = "linear-gradient(90deg, #6366F1 0%, #9F03FF 100%)";
const PRIMARY = "#6366F1";

/**************************
 * Styled Components
 *************************/
const GradientButton = styled(Button)(({ theme }) => ({
  fontFamily: '"Lexend", sans-serif',
  background: GRADIENT,
  color: "#fff",
  fontWeight: 600,
  textTransform: "none",
  borderRadius: theme.shape.borderRadius,
  padding: theme.spacing(1.2, 3),
  "&:hover": {
    background: GRADIENT,
    boxShadow: "0 4px 14px rgba(99, 102, 241, 0.3)",
  },
}));

const ColorConnector = styled(StepConnector)(() => ({
  [`& .${stepConnectorClasses.line}`]: {
    borderColor: alpha(PRIMARY, 0.3),
  },
}));

const GradientStepIcon = ({ active, completed, icon }: StepIconProps) => (
  <Box
    sx={{
      width: 34,
      height: 34,
      borderRadius: "50%",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      fontFamily: '"Lexend", sans-serif',
      fontWeight: 600,
      fontSize: "0.875rem",
      color: "#fff",
      background: active || completed ? GRADIENT : alpha(PRIMARY, 0.25),
    }}
  >
    {icon}
  </Box>
);

/**************************
 * Steps
 *************************/
const steps = [
  {
    label: "Type",
    Component: StepOneBusinessType,
    fields: ["businessType"] as const,
  },
  {
    label: "Details",
    Component: StepTwoBusinessDetails,
    fields: [
      "businessName",
      "country",
      "address",
      "registrationNumber",
      "contactName",
      "contactPhone",
      "contactCountryCode",
      "businessEmail",
      "emailDomain",
      "password",
    ] as const,
  },
  { label: "Review", Component: StepThreeReview, fields: [] as const },
];

interface Props {
  onSubmit: (values: BusinessFormValues) => Promise<void>;
  loading?: boolean;
}

/**************************
 * Component
 *************************/
const BusinessAccountMultiStepForm: React.FC<Props> = ({
  onSubmit,
  loading = false,
}) => {
  const theme = useTheme();
  const [activeStep, setActiveStep] = useState(0);

  const initialValues: BusinessFormValues = {
    businessType: undefined as unknown as "CTP" | "PTP" | "VTP",
    businessName: "",
    country: "",
    address: "",
    registrationNumber: null,
    contactName: "",
    contactPhone: "",
    contactCountryCode: "",
    businessEmail: "",
    emailDomain: "",
    password: "",
  };

  const Current = steps[activeStep].Component;

  const getStepValidationSchema = (stepIdx: number) => {
    if (stepIdx === 0) {
      return yup.object({ businessType: BusinessSchema.fields.businessType });
    } else if (stepIdx === 1) {
      return yup.object({
        businessName: BusinessSchema.fields.businessName,
        country: BusinessSchema.fields.country,
        address: BusinessSchema.fields.address,
        registrationNumber: BusinessSchema.fields.registrationNumber,
        contactName: BusinessSchema.fields.contactName,
        contactPhone: BusinessSchema.fields.contactPhone,
        contactCountryCode: BusinessSchema.fields.contactCountryCode,
        businessEmail: BusinessSchema.fields.businessEmail,
        emailDomain: BusinessSchema.fields.emailDomain,
        password: BusinessSchema.fields.password,
      });
    }
    return BusinessSchema;
  };

  return (
    <Formik
      initialValues={initialValues}
      validationSchema={getStepValidationSchema(activeStep)}
      validateOnBlur={true}
      validateOnChange={false}
      onSubmit={async (values, actions) => {
        if (activeStep < steps.length - 1) {
          const valid = await actions.validateForm();
          if (Object.keys(valid).length === 0) {
            setActiveStep((s) => s + 1);
          } else {
            actions.setTouched(
              Object.fromEntries(steps[activeStep].fields.map((f) => [f, true]))
            );
          }
        } else {
          const valid = await actions.validateForm();
          if (Object.keys(valid).length === 0) {
            await onSubmit(values);
          } else {
            actions.setTouched(
              Object.fromEntries(
                Object.keys(BusinessSchema.fields).map((f) => [f, true])
              )
            );
          }
        }
      }}
    >
      {(formik: FormikProps<BusinessFormValues>) => (
        <Form>
          <Box
            sx={{
              minHeight: "100dvh",
              display: "flex",
              flexDirection: "column",
              bgcolor: alpha(PRIMARY, 0.03),
            }}
          >
            {/* Header */}
            <Box
              sx={{
                py: 2,
                px: 3,
                borderBottom: `1px solid ${alpha(PRIMARY, 0.2)}`,
              }}
            >
              <Typography
                variant="h5"
                fontFamily='"Lexend", sans-serif'
                fontWeight={700}
                sx={{
                  background: GRADIENT,
                  WebkitBackgroundClip: "text",
                  color: "transparent",
                }}
              >
                TAASNet
              </Typography>
            </Box>

            {/* Body */}
            <Container maxWidth="md" sx={{ py: 6, flexGrow: 1 }}>
              <Typography
                variant="h5"
                mb={4}
                textAlign="center"
                fontFamily='"Lexend", sans-serif'
                fontWeight={700}
                sx={{
                  background: GRADIENT,
                  WebkitBackgroundClip: "text",
                  color: "transparent",
                }}
              >
                Business Account Registration
              </Typography>

              <Stepper
                activeStep={activeStep}
                alternativeLabel
                connector={<ColorConnector />}
                sx={{ mb: 6 }}
              >
                {steps.map((s, i) => (
                  <Step key={s.label}>
                    <StepLabel StepIconComponent={GradientStepIcon}>
                      <Typography
                        fontFamily='"Lexend", sans-serif'
                        fontWeight={600}
                        fontSize="0.9rem"
                        sx={{ mt: 1 }}
                        color={
                          activeStep === i
                            ? theme.palette.text.primary
                            : theme.palette.text.secondary
                        }
                      >
                        {s.label}
                      </Typography>
                    </StepLabel>
                  </Step>
                ))}
              </Stepper>

              <Current />

              <Box mt={6} display="flex" justifyContent="space-between">
                <Button
                  disabled={activeStep === 0}
                  onClick={() => setActiveStep((s) => Math.max(0, s - 1))}
                  sx={{ fontFamily: '"Lexend", sans-serif' }}
                  type="button"
                >
                  Back
                </Button>
                <GradientButton type="submit" disabled={loading}>
                  {loading
                    ? "Submitting..."
                    : activeStep === steps.length - 1
                      ? "Submit"
                      : "Next"}
                </GradientButton>
              </Box>
            </Container>

            {/* Footer */}
            <Box
              sx={{
                py: 2,
                textAlign: "center",
                borderTop: `1px solid ${alpha(PRIMARY, 0.2)}`,
              }}
            >
              <Typography
                variant="caption"
                fontFamily='"Lexend", sans-serif'
                color="text.secondary"
              >
                © {new Date().getFullYear()} StageLink. All rights reserved.
              </Typography>
            </Box>
          </Box>
        </Form>
      )}
    </Formik>
  );
};

export default BusinessAccountMultiStepForm;
